<template>
  <div class="glass-panel p-6">
    <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-6">Cost Heatmap - Usage Patterns</h2>
    
    <div class="overflow-x-auto">
      <div class="inline-block min-w-full">
        <!-- Hour labels -->
        <div class="flex mb-2">
          <div class="w-24"></div>
          <div class="flex-1 grid gap-1" style="grid-template-columns: repeat(24, minmax(0, 1fr));">
            <div 
              v-for="hour in 24" 
              :key="hour"
              class="text-xs text-center text-gray-500 dark:text-gray-400"
            >
              {{ hour - 1 }}
            </div>
          </div>
        </div>
        
        <!-- Heatmap grid -->
        <div v-for="day in 7" :key="day" class="flex mb-1">
          <div class="w-24 text-sm flex items-center text-gray-700 dark:text-gray-300 font-medium">
            {{ getDayName(day - 1) }}
          </div>
          <div class="flex-1 grid gap-1" style="grid-template-columns: repeat(24, minmax(0, 1fr));">
            <div 
              v-for="hour in 24" 
              :key="`${day}-${hour}`"
              :style="{ backgroundColor: getCellColor(day - 1, hour - 1) }"
              :title="getCellTooltip(day - 1, hour - 1)"
              class="aspect-square rounded cursor-pointer hover:ring-2 hover:ring-purple-500 transition-all"
              @click="handleCellClick(day - 1, hour - 1)"
            >
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Legend -->
    <div class="mt-6 flex items-center justify-center gap-3">
      <span class="text-sm text-gray-600 dark:text-gray-400">Low Cost</span>
      <div class="flex gap-1">
        <div 
          v-for="i in 10" 
          :key="i" 
          :style="{ backgroundColor: getGradientColor(i - 1) }"
          class="w-8 h-4 rounded"
        ></div>
      </div>
      <span class="text-sm text-gray-600 dark:text-gray-400">High Cost</span>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  data: Array<{
    dayOfWeek: number
    hour: number
    cost: number
  }>
}>()

const emit = defineEmits(['cell-click'])

const { formatCurrency } = useCostFormatting()

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const getDayName = (day: number): string => {
  return dayNames[day] || ''
}

const getCellData = (day: number, hour: number) => {
  return props.data.find(d => d.dayOfWeek === day && d.hour === hour)
}

const maxCost = computed(() => {
  return Math.max(...props.data.map(d => d.cost))
})

const minCost = computed(() => {
  return Math.min(...props.data.map(d => d.cost))
})

const getCellColor = (day: number, hour: number): string => {
  const cellData = getCellData(day, hour)
  if (!cellData) return '#E5E7EB'
  
  const intensity = (cellData.cost - minCost.value) / (maxCost.value - minCost.value)
  
  // Purple to blue gradient
  const r = Math.floor(139 - (139 - 59) * intensity)
  const g = Math.floor(92 - (92 - 130) * intensity)
  const b = Math.floor(246)
  
  return `rgb(${r}, ${g}, ${b})`
}

const getGradientColor = (index: number): string => {
  const intensity = index / 9
  const r = Math.floor(139 - (139 - 59) * intensity)
  const g = Math.floor(92 - (92 - 130) * intensity)
  const b = Math.floor(246)
  
  return `rgb(${r}, ${g}, ${b})`
}

const getCellTooltip = (day: number, hour: number): string => {
  const cellData = getCellData(day, hour)
  if (!cellData) return 'No data'
  
  return `${getDayName(day)} ${hour}:00 - ${formatCurrency(cellData.cost)}`
}

const handleCellClick = (day: number, hour: number) => {
  const cellData = getCellData(day, hour)
  if (cellData) {
    emit('cell-click', cellData)
  }
}
</script>
